// @flow
import React, { useRef, useState, useEffect } from 'react';
import { type FieldProps } from 'formik';
import moment from 'moment';
import classNames from 'classnames';
import { focusNextInput, getValidationStatus } from 'Components/frameworks/formik/inputsUtils';
import shortId from 'shortid';

type Props = {
  label: string,
  isRequired: boolean,
  isDisabled: boolean,
  monthPlaceholder: string,
  yearPlaceholder: string,
  dateFormat: string,
  value: ?number,
  ...FieldProps,
};

export const CreditCardExpiryInput = ({
  label,
  field,
  isRequired,
  isDisabled,
  form,
  ...props
}: Props) => {
  const { isValidated, isErrored } = getValidationStatus(field, form);
  const { current: uniqIdentifier } = useRef(`dateInput-${shortId.generate()}`);
  const refFirstInput = useRef(null);

  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const onChangeMonth = ({ target }) => {
    setMonth(target.value);

    focusNextInput(target);
  };

  const onChangeYear = ({ target }) => {
    setYear(target.value);

    focusNextInput(target);
  };

  const structure = [
    {
      keyPos: 'm',
      name: `${field.name}-CreditCardExpiryInput-month`,
      value: month,
      length: 2,
      handleChange: onChangeMonth,
      placeholder: props.monthPlaceholder,
    },
    {
      keyPos: 'y',
      name: `${field.name}-CreditCardExpiryInput-year`,
      value: year,
      length: 4,
      handleChange: onChangeYear,
      placeholder: props.yearPlaceholder,
    },
  ].sort(
    // sort each input to respect the locale format (MM/YYYY or YYYY/MM ...)
    (i1, i2) =>
      props.dateFormat.toLowerCase().indexOf(i1.keyPos) -
      props.dateFormat.toLowerCase().indexOf(i2.keyPos),
  );

  useEffect(
    () => {
      // if no value is entered, no need to set fields to null
      if (!month && !year) return;

      // when the date is incomplete erase the stored birth date to null to not store an invalid date
      if (!structure.every(({ value, length }) => value.length === length)) {
        /**
         * setFieldValue returns a promise even if the doc says the opposite
         * https://github.com/jaredpalmer/formik/blob/f117c04738ed218b5eb8916d7189e0849962d50d/packages/formik/src/Formik.tsx#L602
         * BUT useEffect does not allow a promise as return...
         */
        form.setFieldValue(field.name, null);
        return;
      }

      const expiryDate = moment([+year, +month - 1]);
      form.setFieldValue(field.name, expiryDate.valueOf());
    },
    [month, year],
  );

  const handleBlur = ({ relatedTarget }) => {
    if (
      !relatedTarget ||
      !relatedTarget.dataset.dateinputidentifier ||
      !relatedTarget.dataset.dateinputidentifier.includes('uniqIdentifier')
    ) {
      form.setFieldTouched(field.name);
    }
  };

  const handleFocus = ({ target }) => {
    if (
      structure.every(({ value }) => value === '') &&
      refFirstInput.current &&
      target !== refFirstInput.current
    ) {
      refFirstInput.current.focus();
    }
  };

  return (
    <div
      className={classNames('flex flex-col', {
        'opacity-50 italic': isDisabled,
      })}
    >
      <div className="flex transition-all relative">
        {structure.map(({ name, handleChange, value, length, placeholder }, key) => (
          <input
            className={classNames('w-full outline-none text-base text-center overflow-hidden', {
              'bg-gray-light cursor-not-allowed': isDisabled,
            })}
            autoComplete="off"
            ref={key === 0 ? refFirstInput : undefined}
            key={name}
            name={name}
            type="tel"
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            maxLength={length}
            value={value}
            placeholder={placeholder}
            disabled={isDisabled}
            data-dateinputidentifier={uniqIdentifier}
          />
        ))}
        <span
          style={{ left: `${(1 / 2) * 100}%` }}
          className={classNames('pointer-events-none absolute flex items-center', {
            'text-emerald': isValidated,
            'text-red': form.touched[field.name] && isErrored,
          })}
        >
          /
        </span>
      </div>
    </div>
  );
};
